import { useEffect, useRef } from "react";
import { SupportTicketMessage } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";
import { useMarkSupportTicketMessagesReadMutation } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";

type UseMessageReadTrackingProps = {
    ticketId: string | undefined;
    messages: SupportTicketMessage[];
    bitrixSessionId: string | undefined;
    messagesListRef: React.RefObject<HTMLUListElement>;
};

export function useMessageReadTracking({
    ticketId,
    messages,
    bitrixSessionId,
    messagesListRef,
}: UseMessageReadTrackingProps) {
    const maxSeenMessageIdRef = useRef<number>(0);
    const [markSupportMessagesRead] = useMarkSupportTicketMessagesReadMutation();

    // отслеживаем просмотр сообщений поддержки и шлём lastSeenMessageId
    useEffect(() => {
        if (!ticketId || !messages.length) return;
        if (!bitrixSessionId) return;

        const list = messagesListRef.current;
        if (!list) return;

        const visibleSupportMessageIds = new Set<number>();

        const updateLastSeenMessage = () => {
            if (visibleSupportMessageIds.size === 0) return;

            const lastVisibleId = Math.max(...Array.from(visibleSupportMessageIds));

            if (lastVisibleId > maxSeenMessageIdRef.current) {
                maxSeenMessageIdRef.current = lastVisibleId;
                markSupportMessagesRead({
                    id: ticketId,
                    lastSeenMessageId: lastVisibleId,
                    sessid: bitrixSessionId,
                }).catch(() => {
                    // Игнорируем ошибки при пометке сообщений как прочитанных
                });
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const target = entry.target as HTMLElement;
                    const idAttr = target.getAttribute("data-message-id");
                    const bySupport = target.getAttribute("data-message-by-support");

                    if (bySupport !== "Y" || !idAttr) return;

                    const id = Number(idAttr);
                    if (!Number.isFinite(id)) return;

                    if (entry.isIntersecting) {
                        visibleSupportMessageIds.add(id);
                    } else {
                        visibleSupportMessageIds.delete(id);
                    }
                });

                updateLastSeenMessage();
            },
            {
                root: list,
                threshold: 0.6,
            },
        );

        const items = list.querySelectorAll<HTMLElement>("[data-message-id]");
        items.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [ticketId, messages, bitrixSessionId, messagesListRef, markSupportMessagesRead]);

    return { maxSeenMessageIdRef };
}


